import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { categoryIdForPath, isSamePath, searchAdminPages } from '@/constants/adminNavCategories';

/** Which categories this device left open, kept across visits. */
export const ADMIN_NAV_OPEN_KEY = 'defera.adminNav.open';
/** Open on a first visit: the queue admins work all day. */
const DEFAULT_OPEN = ['concierge'];

function readOpen() {
  try {
    const raw = JSON.parse(localStorage.getItem(ADMIN_NAV_OPEN_KEY) || 'null');
    return Array.isArray(raw) ? raw.filter((id) => typeof id === 'string') : DEFAULT_OPEN;
  } catch {
    return DEFAULT_OPEN;
  }
}

function writeOpen(ids) {
  try {
    localStorage.setItem(ADMIN_NAV_OPEN_KEY, JSON.stringify(ids));
  } catch {
    /* private mode or blocked storage: the menu still works, it just won't remember */
  }
}

const ROW = 'group flex items-center gap-3 w-full rounded-lg px-3 py-2.5 text-left transition-colors';
const PAGE = 'flex items-center gap-2.5 w-full rounded-lg px-3 py-2 text-[15px] font-normal transition-colors';

/**
 * The admin pages as a console menu (mobile drawer): a search field, then one
 * collapsible row per area with its pages inside. The area holding the page
 * you're on opens on its own; the rest stay as you left them on this device.
 * Typing in search shows every matching page across areas, flat.
 *
 * `wrapItem(item, link)` lets the drawer wrap each link (SheetClose, so a tap
 * both navigates and closes it).
 */
export default function AdminNavCategories({ categories, pathname, wrapItem = (_item, link) => link, showSearch = true }) {
  const activeId = categoryIdForPath(categories, pathname);
  const known = React.useMemo(() => new Set(categories.map((c) => c.id)), [categories]);
  const [open, setOpen] = React.useState(() => {
    // One category (an investor's Analytics, the desk role's Inquiries) is
    // simply open: a page one tap away shouldn't sit behind a fold.
    if (categories.length === 1) return [categories[0].id];
    const ids = readOpen().filter((id) => known.has(id));
    return activeId && !ids.includes(activeId) ? [...ids, activeId] : ids;
  });
  const [query, setQuery] = React.useState('');

  // Navigating to a page in a closed area opens it, so where you are is always on screen.
  React.useEffect(() => {
    if (activeId) setOpen((ids) => (ids.includes(activeId) ? ids : [...ids, activeId]));
  }, [activeId]);

  const onOpenChange = (ids) => {
    setOpen(ids);
    writeOpen(ids);
  };

  const pageLink = (item, { category = null } = {}) => {
    const active = isSamePath(item.url, pathname);
    const link = (
      <Link
        to={item.url}
        aria-current={active ? 'page' : undefined}
        className={cn(PAGE, active ? 'bg-white/[0.08] text-white' : 'text-white/75 hover:bg-white/[0.06] hover:text-white')}
      >
        <item.icon className={cn('w-4 h-4 flex-shrink-0', active ? 'text-[hsl(228,35%,68%)]' : 'text-white/35')} />
        <span className="truncate">{item.title}</span>
        {category && <span className="ml-auto pl-2 text-xs text-white/35 whitespace-nowrap">{category}</span>}
      </Link>
    );
    return <React.Fragment key={`${category || ''}${item.url}`}>{wrapItem(item, link)}</React.Fragment>;
  };

  const needle = query.trim();
  const matches = searchAdminPages(categories, query);

  return (
    <div>
      {showSearch && (
        <div className="relative px-1 pb-2">
          <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-[calc(50%+4px)] w-4 h-4 text-white/35" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find an admin page"
            aria-label="Find an admin page"
            className="w-full h-10 rounded-lg bg-white/[0.04] border border-white/[0.08] pl-9 pr-9 text-[15px] font-normal text-white placeholder:text-white/35 focus:outline-none focus:border-white/20 [&::-webkit-search-cancel-button]:hidden"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-[calc(50%+4px)] p-1 rounded-md text-white/40 hover:text-white hover:bg-white/[0.06]"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {needle ? (
        <div role="list" aria-label="Matching admin pages">
          {matches.length > 0
            ? matches.map(({ item, category }) => pageLink(item, { category }))
            : <p className="px-3 py-3 text-sm font-normal text-white/45">No admin page matches “{query.trim()}”.</p>}
        </div>
      ) : (
        <AccordionPrimitive.Root type="multiple" value={open} onValueChange={onOpenChange}>
          {categories.map((c) => (
            <AccordionPrimitive.Item key={c.id} value={c.id}>
              <AccordionPrimitive.Header className="flex">
                <AccordionPrimitive.Trigger className={cn(ROW, 'text-white/90 hover:bg-white/[0.05] [&[data-state=open]_.chev]:rotate-180')}>
                  {/* The icon alone, no tile behind it; the 28px slot keeps it
                      over the line its pages hang from. */}
                  <span className="inline-flex items-center justify-center w-7 h-7 flex-shrink-0">
                    <c.icon className={cn('w-4 h-4', c.id === activeId ? 'text-[hsl(228,45%,78%)]' : 'text-white/55')} />
                  </span>
                  <span className="text-base font-normal">{c.title}</span>
                  <span className="ml-auto text-xs font-normal text-white/35 tabular-nums">{c.items.length}</span>
                  <ChevronDown className="chev w-4 h-4 text-white/35 transition-transform duration-200" />
                </AccordionPrimitive.Trigger>
              </AccordionPrimitive.Header>
              <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                <div className="ml-[26px] pl-2 mb-1 border-l border-white/[0.07]">
                  {c.items.map((item) => pageLink(item))}
                </div>
              </AccordionPrimitive.Content>
            </AccordionPrimitive.Item>
          ))}
        </AccordionPrimitive.Root>
      )}
    </div>
  );
}

AdminNavCategories.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
    })).isRequired,
  })).isRequired,
  pathname: PropTypes.string,
  wrapItem: PropTypes.func,
  showSearch: PropTypes.bool,
};
